import { TRBStatusTableRow, TRBMaxRiskTableRow } from 'src/features/types/trbTypes';

export type SortOrder = 'asc' | 'desc';

/**
 * Computes timeToCPT string and timeToCPTSeconds from a cptUTC string.
 * The cptUTC format is "MMM d, HH:mm UTC" (e.g. "Aug 14, 10:30 UTC").
 * Returns { timeToCPT: "Xd Xh Xm", timeToCPTSeconds: number }
 */
export function computeTimeToCPT(cptUTC: string): { timeToCPT: string; timeToCPTSeconds: number } {
    try {
        // Parse "MMM d, HH:mm UTC" format — append current year since it's not in the string
        const cleanedCpt = cptUTC.replace(' UTC', '');
        const currentYear = new Date().getFullYear();
        const cptDate = new Date(`${cleanedCpt}, ${currentYear} UTC`);

        if (isNaN(cptDate.getTime())) {
            return { timeToCPT: '0m', timeToCPTSeconds: 0 };
        }

        const nowMs = Date.now();
        const diffMs = cptDate.getTime() - nowMs;
        const diffSeconds = Math.floor(diffMs / 1000);

        if (diffSeconds <= 0) {
            return { timeToCPT: '0m', timeToCPTSeconds: diffSeconds };
        }

        const totalMinutes = Math.floor(diffSeconds / 60);
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;

        let timeToCPT = '';
        if (days !== 0) timeToCPT += `${days}d `;
        if (hours !== 0) timeToCPT += `${hours}h `;
        if (minutes !== 0 || timeToCPT.length === 0) timeToCPT += `${minutes}m`;

        return { timeToCPT: timeToCPT.trim(), timeToCPTSeconds: diffSeconds };
    } catch {
        return { timeToCPT: '0m', timeToCPTSeconds: 0 };
    }
}

export function riskComparison(a: string, b: string, order: SortOrder): number {
    if (a === b) return 0;

    let riskAFloat = parseFloat(a);
    let riskBFloat = parseFloat(b);

    if (isNaN(riskAFloat)) riskAFloat = Number.MAX_VALUE;
    if (isNaN(riskBFloat)) riskBFloat = Number.MAX_VALUE;

    let rank = riskAFloat - riskBFloat;
    if (Math.abs(rank) < 0.0001) return 0;

    return order !== 'desc' ? rank : rank * -1.0;
}

export function timeToCPTComparison(a: number, b: number, order: SortOrder): number {
    const rank = a - b;
    return order !== 'desc' ? rank : rank * -1.0;
}

export function cptComparison(a: string, b: string, order: SortOrder): number {
    if (a === b) return 0;
    const rank = a > b ? 1 : -1;
    return order !== 'desc' ? rank : rank * -1.0;
}

export function highThresholdMinusRiskComparison(a: string, b: string, order: SortOrder): number {
    if (a === b) return 0;

    let differenceAFloat = parseFloat(a);
    let differenceBFloat = parseFloat(b);

    if (isNaN(differenceAFloat)) differenceAFloat = Number.MIN_VALUE;
    if (isNaN(differenceBFloat)) differenceBFloat = Number.MIN_VALUE;

    let rank = differenceAFloat - differenceBFloat;
    if (Math.abs(rank) < 0.0001) return 0;

    return order !== 'desc' ? rank : rank * -1.0;
}

export type SortField = 'fc' | 'cptUTC' | 'cpt' | 'timeToCPT' | 'lowThreshold' | 'highThreshold' | 'highThresholdMinusRisk' | 'risk' | 'processPathGroup';

export function sortStatusTableData(
    data: TRBStatusTableRow[],
    sortField: SortField,
    sortOrder: SortOrder,
): TRBStatusTableRow[] {
    return [...data].sort((a, b) => {
        switch (sortField) {
            case 'risk':
                return riskComparison(a.risk, b.risk, sortOrder);
            case 'timeToCPT':
                return timeToCPTComparison(a.timeToCPTSeconds, b.timeToCPTSeconds, sortOrder);
            case 'cpt':
            case 'cptUTC':
                return cptComparison(
                    sortField === 'cptUTC' ? a.cptUTC : a.cpt,
                    sortField === 'cptUTC' ? b.cptUTC : b.cpt,
                    sortOrder,
                );
            default: {
                const valA = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
                const valB = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
                const rank = valA.localeCompare(valB);
                return sortOrder !== 'desc' ? rank : rank * -1;
            }
        }
    });
}

export function sortMaxRiskTableData(
    data: TRBMaxRiskTableRow[],
    sortField: SortField,
    sortOrder: SortOrder,
): TRBMaxRiskTableRow[] {
    return [...data].sort((a, b) => {
        switch (sortField) {
            case 'risk':
                return riskComparison(a.risk, b.risk, sortOrder);
            case 'timeToCPT':
                return timeToCPTComparison(a.timeToCPTSeconds, b.timeToCPTSeconds, sortOrder);
            case 'highThresholdMinusRisk':
                return highThresholdMinusRiskComparison(a.highThresholdMinusRisk, b.highThresholdMinusRisk, sortOrder);
            case 'cpt':
            case 'cptUTC':
                return cptComparison(
                    sortField === 'cptUTC' ? a.cptUTC : a.cpt,
                    sortField === 'cptUTC' ? b.cptUTC : b.cpt,
                    sortOrder,
                );
            default: {
                const valA = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
                const valB = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
                const rank = valA.localeCompare(valB);
                return sortOrder !== 'desc' ? rank : rank * -1;
            }
        }
    });
}
